import { computed } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { SMALL_SCREEN_BREAKPOINT } from 'dashboard/constants/breakpoints';
import wootConstants from 'dashboard/constants/globals';

const { LAYOUT_TYPES } = wootConstants;

// The table layout is scoped to the main conversations queue; inbox, label,
// team and other filtered routes keep the column layouts only.
export const TABLE_LAYOUT_ROUTES = ['home', 'inbox_conversation'];

/**
 * The conversation layout in effect right now.
 *
 * A phone-width viewport has no room for a list beside a thread, so the list
 * always renders expanded below the small-screen breakpoint. That is a fact
 * about the viewport, not a choice the agent made: the stored
 * `conversation_display_type` is never rewritten, so whatever they picked -
 * table included - is back the moment there is room for it again.
 *
 * @returns {Object} The layout in effect and whether it is the expanded one.
 */
export function useConversationDisplayType() {
  const { uiSettings, updateUISettings } = useUISettings();
  const { width: windowWidth } = useWindowSize();

  const conversationDisplayType = computed(() => {
    if (windowWidth.value < SMALL_SCREEN_BREAKPOINT) {
      return LAYOUT_TYPES.EXPANDED;
    }
    return uiSettings.value.conversation_display_type || LAYOUT_TYPES.CONDENSED;
  });

  // An explicit check so a stored table preference does not read as expanded on
  // the routes that never render the table layout.
  const isOnExpandedLayout = computed(
    () => conversationDisplayType.value === LAYOUT_TYPES.EXPANDED
  );

  // One writer for the list ⇄ table switch, shared by the header toggle and
  // the `L` shortcut. Entering the table keeps the stored column preference
  // untouched, so leaving it lands back on the layout the agent had before.
  const toggleTableLayout = () => {
    const {
      conversation_display_type: currentDisplayType = LAYOUT_TYPES.CONDENSED,
      previously_used_conversation_display_type:
        previousDisplayType = LAYOUT_TYPES.CONDENSED,
    } = uiSettings.value;
    updateUISettings({
      conversation_display_type:
        currentDisplayType === LAYOUT_TYPES.TABLE
          ? previousDisplayType
          : LAYOUT_TYPES.TABLE,
    });
  };

  return { conversationDisplayType, isOnExpandedLayout, toggleTableLayout };
}
